import { CanvasTexture, ClampToEdgeWrapping, Color, DoubleSide, LinearFilter, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, RepeatWrapping, Scene, SphereGeometry, Texture, UVMapping, WebGLRenderer } from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { decompress } from 'three/examples/jsm/utils/TextureUtils.js';

import type { ExportManager } from '../export-manager.class';
import type { ModelExportOptions } from '../types';
import { triggerDownload } from '../helpers';
import { ExportSceneUnavailableError, ExportValidationError } from '../../../types/export-errors';
import { validateGlbOutputCfn } from './validate-glb-output.cfn';

/** Module-level cache: memoize tone-mapped LDR canvas per HDR source texture. */
const toneMapCache = new WeakMap<Texture, CanvasTexture>();

/**
 * Mark opaque color textures for JPEG encoding before GLTFExporter serializes them.
 * `userData.mimeType` is renderer-inert — Three.js ignores it at draw time.
 * The clone() call in buildCleanScene does NOT deep-clone materials/textures, so
 * mutating userData here also affects the live scene's texture objects, which is safe
 * because the renderer never reads userData.
 * JPEG must not touch non-color data maps (normal/roughness/metalness/displacement/alpha)
 * — JPEG artifacts corrupt those channels and cause visible rendering defects.
 */
/** Decompressed-KTX2 cache: pay the GPU readback once per source texture per session, and keep our JPEG mark (the exporter's internal decompress would create a fresh texture and lose it). */
const decompressedTextureCache = new WeakMap<Texture, Texture>();

// decompress() creates a WebGLRenderer per call when none is passed — leaked contexts exhaust the
// browser cap (~16) and permanently kill later canvases. One shared instance, released after export.
let decompressRenderer: WebGLRenderer | null = null;
const getDecompressRenderer = (): WebGLRenderer => {
	if (!decompressRenderer) decompressRenderer = new WebGLRenderer();
	return decompressRenderer;
};

const toExportableColorTexture = (texture: Texture): Texture => {
	if (!(texture as unknown as { isCompressedTexture?: boolean }).isCompressedTexture) return texture;
	const cached = decompressedTextureCache.get(texture);
	if (cached) return cached;
	const plain = decompress(texture, 1024, getDecompressRenderer());
	// decompress() drops UV transform state — without this the floor/surround tiling exports at the wrong scale
	plain.wrapS = texture.wrapS;
	plain.wrapT = texture.wrapT;
	plain.repeat.copy(texture.repeat);
	plain.offset.copy(texture.offset);
	plain.rotation = texture.rotation;
	plain.needsUpdate = true;
	plain.userData.mimeType = 'image/jpeg';
	decompressedTextureCache.set(texture, plain);
	return plain;
};

/** Downscaled normal-map cache (JPEG at 1024px — chroma smoothing is invisible on stone/wood noise and ~10x smaller than the RGBA PNG the exporter writes by default). */
const downscaledNormalCache = new WeakMap<Texture, Texture>();
const EXPORT_NORMAL_MAP_SIZE = 1024;

const toExportableNormalTexture = (texture: Texture): Texture => {
	const cached = downscaledNormalCache.get(texture);
	if (cached) return cached;
	const source = (texture as unknown as { isCompressedTexture?: boolean }).isCompressedTexture ? decompress(texture, EXPORT_NORMAL_MAP_SIZE, getDecompressRenderer()) : texture;
	const image = source.image as { width?: number; height?: number } | undefined;
	if (!image?.width || image.width <= EXPORT_NORMAL_MAP_SIZE) {
		source.userData.mimeType = 'image/jpeg';
		downscaledNormalCache.set(texture, source);
		return source;
	}
	const scale = EXPORT_NORMAL_MAP_SIZE / image.width;
	const canvas = document.createElement('canvas');
	canvas.width = EXPORT_NORMAL_MAP_SIZE;
	canvas.height = Math.round((image.height ?? image.width) * scale);
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		source.userData.mimeType = 'image/jpeg';
		downscaledNormalCache.set(texture, source);
		return source;
	}
	ctx.drawImage(source.image as CanvasImageSource, 0, 0, canvas.width, canvas.height);
	const small = new CanvasTexture(canvas);
	small.wrapS = source.wrapS;
	small.wrapT = source.wrapT;
	small.repeat.copy(source.repeat);
	small.offset.copy(source.offset);
	small.flipY = source.flipY;
	small.needsUpdate = true;
	small.userData.mimeType = 'image/jpeg';
	downscaledNormalCache.set(texture, small);
	return small;
};

const markColorTexturesForJpeg = (root: Object3D): void => {
	root.traverse((child) => {
		const mesh = child as Mesh;
		if (!mesh.isMesh || !mesh.material) return;

		const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
		for (const mat of materials) {
			const m = mat as MeshStandardMaterial & { isMeshBasicMaterial?: boolean };
			if (!(m as MeshStandardMaterial).isMeshStandardMaterial && !m.isMeshBasicMaterial) continue;

			// Black emissive contributes nothing — a Collada artifact that would double-export every color texture
			if (m.emissiveMap && (m.emissive?.getHex() ?? 0) === 0) m.emissiveMap = null;

			if (m.normalMap) m.normalMap = toExportableNormalTexture(m.normalMap);

			// Collada materials often carry transparent=true with opacity=1 (a no-op) — only real alpha use disqualifies JPEG
			if (m.alphaMap || (m.alphaTest ?? 0) > 0 || (m.opacity ?? 1) < 1) continue;

			if (m.map) {
				m.map = toExportableColorTexture(m.map);
				if (!m.map.userData.mimeType) m.map.userData.mimeType = 'image/jpeg';
			}
			if (m.emissiveMap) {
				m.emissiveMap = toExportableColorTexture(m.emissiveMap);
				if (!m.emissiveMap.userData.mimeType) m.emissiveMap.userData.mimeType = 'image/jpeg';
			}
		}
	});
};

/** Padding around kitchen footprint for the floor plane (in scene units before scale). */
const FLOOR_PADDING = 48; // 48 inches = 4 feet of floor around the kitchen

/**
 * Sky dome radius in scene units (inches before scale). The dome lives inside
 * the kitchen `clone` group, so the inch→meter scale (0.0254) applies — final
 * radius is about (this × 0.0254) meters.
 *
 * Sized to match the foundation floor's 1000×1000-inch extent (≈25.4 m) so the
 * dome doesn't enlarge model-viewer's bounding box beyond what the floor
 * already contributes. The consuming kitchen-viewer's camera-fit logic uses
 * the smallest scene dimension to estimate kitchen height; a 1270 m dome
 * forced that estimate to ~2540 m and parked the camera 8000 m out, rendering
 * the kitchen as a speck inside a white sphere.
 *
 * The kitchen-viewer also caps `max-camera-orbit` so the user can't zoom
 * past this distance — see the viewer for the matching bound.
 */
const SKY_DOME_RADIUS = 500;

/**
 * Upgrade non-MeshStandardMaterial materials to MeshStandardMaterial so the
 * GLTFExporter serializes correct PBR metalness/roughness values.
 *
 * ColladaLoader produces MeshPhongMaterial / MeshLambertMaterial. The live
 * scene sets metalness/roughness as JS properties and applies envMap for
 * reflections, but the GLTFExporter only reads PBR properties from
 * MeshStandardMaterial instances — non-standard materials export as
 * metalness=0, roughness=1 (flat plastic).
 */
const upgradeMaterialsForExport = (root: Object3D): void => {
	root.traverse((child) => {
		const mesh = child as Mesh;
		if (!mesh.isMesh || !mesh.material) return;

		const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
		const upgraded = materials.map((mat: any) => {
			if (mat.isMeshStandardMaterial) return mat;

			const std = new MeshStandardMaterial({
				color: mat.color ?? new Color(0xffffff),
				map: mat.map ?? null,
				normalMap: mat.normalMap ?? null,
				emissive: mat.emissive ?? new Color(0x000000),
				emissiveMap: mat.emissiveMap ?? null,
				emissiveIntensity: mat.emissiveIntensity ?? 1,
				alphaMap: mat.alphaMap ?? null,
				transparent: mat.transparent ?? false,
				opacity: mat.opacity ?? 1,
				side: mat.side ?? DoubleSide,
				name: mat.name
			});

			// Transfer metalness/roughness if set (adjustSingleMaterial adds
			// these as own properties on PhongMaterial for bare-metal names)
			const nameLower = (mat.name || '').toLowerCase();
			const hasTexture = mat.map != null;
			const isLikelyMetal =
				(typeof mat.metalness === 'number' && mat.metalness > 0.3) ||
				(!hasTexture &&
					(nameLower.includes('steel') ||
						nameLower.includes('metal') ||
						nameLower.includes('brushe') ||
						nameLower.includes('aluminum') ||
						nameLower.includes('chrome') ||
						nameLower.includes('stainless') ||
						nameLower.includes('inox')));

			if (isLikelyMetal) {
				std.metalness = typeof mat.metalness === 'number' ? mat.metalness : 0.8;
				std.roughness = typeof mat.roughness === 'number' ? mat.roughness : 0.15;
			} else {
				std.metalness = typeof mat.metalness === 'number' ? mat.metalness : 0;
				std.roughness = typeof mat.roughness === 'number' ? mat.roughness : 0.8;
			}

			std.needsUpdate = true;
			return std;
		});

		mesh.material = Array.isArray(mesh.material) ? upgraded : upgraded[0];
	});
};

/**
 * Resolve the active sky texture for export. Prefers the live `scene.background`
 * (which holds the equirectangular Texture once a sky has been loaded), then
 * falls back to looking up `skyTextureKey` if a delegate populated it but the
 * background was reset (e.g. background color mode).
 *
 * Returns null when no usable Texture is available — the caller skips the dome
 * rather than emit a meshless export.
 */
const resolveSkyTexture = (self: ExportManager): Texture | null => {
	const { scene } = self.configAccess;
	const bg = scene?.background;
	if (bg instanceof Texture) return bg;
	return null;
};

/**
 * Build an inward-facing sky dome carrying the equirectangular sky texture so
 * the exported GLB renders against the same sky the user picked in-app, without
 * relying on the consuming viewer to override `skybox-image`.
 *
 * The dome is parented to the kitchen `clone` so the inch→meter scale applied
 * to the clone scales the dome too. Returned mesh is tagged so it survives the
 * debug-strip pass and is identifiable in downstream viewers.
 */
/**
 * Convert a (potentially HDR) Texture to an LDR CanvasTexture that survives
 * the GLTFExporter's PNG/JPEG serialization without clamping the whole image
 * to white.
 *
 * Sky HDRs store pixel values >1.0 (the sun, horizon glow). GLTFExporter
 * extracts a texture by drawing it onto a 2D canvas and calling toDataURL —
 * canvas 2D contexts only deal in 0-255 unorm, so floats above 1.0 clip to
 * 255. The result is a pure-white texture and a baked dome that looks blown
 * out. Apply a Reinhard-style tone map first so the highlights compress into
 * the 0-1 range and the dome carries the real sky colors.
 */
const toneMapHdrToLdrCanvas = (texture: Texture): CanvasTexture | null => {
	const cached = toneMapCache.get(texture);
	if (cached) return cached;
	const image = texture.image as unknown as {
		data?: Float32Array | Uint16Array | Uint8Array;
		width?: number;
		height?: number;
	} | HTMLImageElement | HTMLCanvasElement | ImageBitmap | null;
	if (!image) return null;

	const dataImage = image as { data?: Float32Array | Uint16Array | Uint8Array; width?: number; height?: number };
	const isHdrData = dataImage.data instanceof Float32Array || dataImage.data instanceof Uint16Array;

	if (!isHdrData) {
		// Already LDR (HTMLImageElement / ImageBitmap / canvas) — let GLTFExporter
		// handle it as-is by returning null so the caller falls back to the raw texture.
		return null;
	}

	const width = dataImage.width!;
	const height = dataImage.height!;
	const src = dataImage.data!;
	const channels = src.length / (width * height);

	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext('2d');
	if (!ctx) return null;

	const out = ctx.createImageData(width, height);
	// Exposure compensation roughly matching model-viewer's `exposure="1.4"`
	// plus Reinhard tone-mapping. Tuned empirically against blouberg_sunrise so
	// the rendered dome roughly matches the in-app live view.
	const EXPOSURE = 1.4;
	const GAMMA_INV = 1 / 2.2;
	for (let i = 0, p = 0; i < width * height; i++) {
		let r = 0, g = 0, b = 0;
		if (channels === 4) {
			r = Number(src[i * 4]); g = Number(src[i * 4 + 1]); b = Number(src[i * 4 + 2]);
		} else if (channels === 3) {
			r = Number(src[i * 3]); g = Number(src[i * 3 + 1]); b = Number(src[i * 3 + 2]);
		}
		// Half-float (Uint16) needs unpacking; we just take the bits as approximate
		// magnitude — RGBELoader's normal output is Float32 anyway.
		if (dataImage.data instanceof Uint16Array) {
			// Quick half-float decode (approximate; good enough for tone-mapping)
			r = halfToFloat(r); g = halfToFloat(g); b = halfToFloat(b);
		}
		r *= EXPOSURE; g *= EXPOSURE; b *= EXPOSURE;
		// Reinhard
		r = r / (1 + r); g = g / (1 + g); b = b / (1 + b);
		// Gamma 2.2
		r = Math.pow(Math.max(0, r), GAMMA_INV);
		g = Math.pow(Math.max(0, g), GAMMA_INV);
		b = Math.pow(Math.max(0, b), GAMMA_INV);
		out.data[p++] = Math.min(255, Math.max(0, Math.round(r * 255)));
		out.data[p++] = Math.min(255, Math.max(0, Math.round(g * 255)));
		out.data[p++] = Math.min(255, Math.max(0, Math.round(b * 255)));
		out.data[p++] = 255;
	}
	ctx.putImageData(out, 0, 0);

	// The dome is a soft backdrop — 1024 wide is visually identical and ~4x smaller in the GLB
	const MAX_SKY_WIDTH = 1024;
	let finalCanvas = canvas;
	if (canvas.width > MAX_SKY_WIDTH) {
		const scaled = document.createElement('canvas');
		scaled.width = MAX_SKY_WIDTH;
		scaled.height = Math.round(canvas.height * (MAX_SKY_WIDTH / canvas.width));
		const sctx = scaled.getContext('2d');
		if (sctx) {
			sctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
			finalCanvas = scaled;
		}
	}

	const ct = new CanvasTexture(finalCanvas);
	ct.mapping = UVMapping;
	ct.wrapS = RepeatWrapping;
	ct.wrapT = ClampToEdgeWrapping;
	ct.minFilter = LinearFilter;
	ct.magFilter = LinearFilter;
	// flipY=false — GLTFExporter honors this flag by pre-flipping the canvas
	// when writing the PNG. Combined with model-viewer's default flipY=true on
	// load, the round-trip cancels out and the sphere's UV layout stays
	// consistent with the source HDR (sky at top row → sky at sphere top). The
	// "upside-down" case is handled by rotating the dome geometry (see
	// buildSkyDome) rather than by fiddling with flipY, so a single sign lives
	// in one place.
	ct.flipY = false;
	ct.needsUpdate = true;
	ct.name = texture.name ? `${texture.name}-ldr` : 'sky-dome-ldr';
	// Sky dome is opaque — JPEG is safe here and shrinks the biggest texture in the GLB.
	ct.userData.mimeType = 'image/jpeg';
	toneMapCache.set(texture, ct);
	return ct;
};

const halfToFloat = (h: number): number => {
	const s = (h & 0x8000) >> 15;
	const e = (h & 0x7C00) >> 10;
	const f = h & 0x03FF;
	if (e === 0) return (s ? -1 : 1) * Math.pow(2, -14) * (f / 1024);
	if (e === 0x1F) return f ? NaN : ((s ? -1 : 1) * Infinity);
	return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + f / 1024);
};

const buildSkyDome = (skyTexture: Texture): Mesh => {
	// Try to tone-map the HDR to LDR for PNG serialization. If the texture is
	// already LDR (HTMLImage/Canvas/ImageBitmap), the helper returns null and
	// we fall back to a plain clone.
	const tonemapped = toneMapHdrToLdrCanvas(skyTexture);
	const cloned = tonemapped ?? skyTexture.clone();
	if (!tonemapped) {
		// The live scene.background texture is tagged with EquirectangularReflectionMapping
		// because three's background pass samples via world view direction. For a sky-dome
		// mesh we want plain UV sampling — SphereGeometry's UVs already lay out an
		// equirectangular image correctly across a sphere. Clone so we don't mutate the
		// live texture's mapping (which would break in-app background sampling).
		cloned.mapping = UVMapping;
		cloned.needsUpdate = true;
		// Sky dome is opaque — JPEG is safe here and shrinks the biggest texture in the GLB.
		cloned.userData.mimeType = 'image/jpeg';
	}

	const geometry = new SphereGeometry(SKY_DOME_RADIUS, 64, 32);
	// SphereGeometry's default UV layout has v=1 at the top vertex. Combined
	// with an equirectangular sky texture (row 0 = sky, last row = ground),
	// the naive mapping puts sky at the sphere BOTTOM once the round-trip
	// through GLTFExporter → PNG → model-viewer plays out. Rotating the
	// geometry 180° around the X axis flips the sphere so v=1 sits at the
	// bottom, which is what the rest of the pipeline expects — and it puts the
	// sky visibly overhead when the design-package viewer orbits.
	geometry.rotateX(Math.PI);
	// DoubleSide (not BackSide) because the GLB serializer maps Three.js `side`
	// onto glTF's `doubleSided` boolean — there's no glTF representation for
	// BackSide-only. Without DoubleSide, the dome silently re-imports as
	// FrontSide and is culled when viewed from inside (camera default position
	// inside the sphere), so the sky disappears and the background goes black.
	const material = new MeshBasicMaterial({
		map: cloned,
		side: DoubleSide,
		toneMapped: false,
		name: 'sky-dome'
	});
	const dome = new Mesh(geometry, material);
	dome.userData = { id: 'sky', type: 'sky', isFoundation: true };
	dome.name = 'sky-dome';
	return dome;
};

/**
 * Build a clean scene containing only kitchen geometry from the objectHolder.
 * Strips debug objects, camera helpers, rotation gizmos. When includeFloor is
 * true, replaces the massive default floor with a smaller one that extends
 * just beyond the kitchen footprint. When includeSky is true, adds an
 * inward-facing sky dome carrying the current equirectangular sky texture.
 */
const buildCleanScene = (self: ExportManager, scale: number, includeFloor: boolean, includeSky: boolean): Scene | null => {
	const config = self.configAccess;
	const { islandHolder, objectHolder, carpet } = config;

	if (!islandHolder) return null;

	const clone = islandHolder.clone(true);
	const tempScene = new Scene();
	tempScene.add(clone);

	// Also include objectHolder contents (foundation floor, etc.) if present.
	if (objectHolder && objectHolder.children.length > 0) {
		const objectClone = objectHolder.clone(true);
		tempScene.add(objectClone);
	}

	// Remove debug junk from cloned islandHolder and objectHolder.
	const toRemove: Array<Object3D> = [];
	tempScene.traverse((child) => {
		const type = (child.userData as Record<string, unknown>)?.type as string | undefined;
		if (type === 'debugVisualization') toRemove.push(child);
		if (type === 'helper') toRemove.push(child);
		if (type === 'rotation-gizmo') toRemove.push(child);
		if (!includeFloor && type === 'floor') toRemove.push(child);
	});
	toRemove.forEach((child) => child.parent?.remove(child));

	// Upgrade non-PBR materials so GLTFExporter preserves metallic reflectance
	upgradeMaterialsForExport(clone);

	if (includeSky) {
		const skyTexture = resolveSkyTexture(self);
		if (skyTexture) {
			clone.add(buildSkyDome(skyTexture));
		} else {
			const skyKey = self.configAccess.skyTextureKey;
			const skyExpected = skyKey && skyKey !== 'background';
			if (skyExpected) {
				throw new ExportSceneUnavailableError(
					`[GLB export] includeSky=true but sky texture not loaded (key="${skyKey}")`
				);
			}
			console.warn('[GLB export] includeSky=true but no Texture found on scene.background — skipping sky dome');
		}
	}

	clone.scale.set(scale, scale, scale);
	tempScene.updateMatrixWorld(true);

	return tempScene;
};

/**
 * Give every mesh in the export scene its own material instance. Object3D.clone
 * shares materials with the live scene, and markColorTexturesForJpeg swaps
 * texture maps on whatever material it finds — so without this step an export
 * taken from the interactive view leaves the live kitchen rendering the
 * downscaled export textures. Material.clone keeps texture references, which
 * is what we want: only the map *assignments* are isolated.
 */
const isolateSceneMaterials = (root: Object3D): void => {
	root.traverse((child) => {
		const mesh = child as Mesh;
		if (!mesh.isMesh || !mesh.material) return;
		mesh.material = Array.isArray(mesh.material)
			? mesh.material.map((mat) => mat.clone())
			: mesh.material.clone();
	});
};

/**
 * Export the scene as a GLB/GLTF file
 */
export const exportGLBCfn = async (
	self: ExportManager,
	options: ModelExportOptions = {}
): Promise<ArrayBuffer | null> => {
	const config = self.configAccess;
	const { scene, carpet, backgroundColor, skyTextureKey, environmentMapTexture } = config;
	const {
		includeFloor = false,
		includeSky = false,
		format = 'glb',
		filename = 'scene.glb',
		scale = 0.0254, // inches to meters
		maxTextureSize = 4096,
		skipDownload = false,
		isolateMaterials = false
	} = options;

	if (!scene) {
		if (skipDownload) throw new ExportSceneUnavailableError('ExportManager.exportGLB: Scene not available');
		console.warn('ExportManager.exportGLB: Scene not available');
		return null;
	}

	// For headless/programmatic export (skipDownload), build a clean scene
	// containing only kitchen geometry — no debug objects, lights, helpers, or floor.
	if (skipDownload) {
		const cleanScene = buildCleanScene(self, scale, includeFloor, includeSky);
		if (!cleanScene) {
			throw new ExportSceneUnavailableError('ExportManager.exportGLB: islandHolder not available for clean export');
		}

		if (isolateMaterials) {
			isolateSceneMaterials(cleanScene);
		}

		markColorTexturesForJpeg(cleanScene);

		const exporter = new GLTFExporter();
		const result = await new Promise<ArrayBuffer | object>((resolve, reject) => {
			exporter.parse(
				cleanScene,
				(data) => resolve(data as ArrayBuffer | object),
				(error) => reject(error),
				{ trs: false, onlyVisible: true, maxTextureSize, binary: format === 'glb' }
			);
		});
		// Free the decompress renderer's GL context — leaked contexts exhaust the browser cap and kill later canvases
		if (decompressRenderer) {
			decompressRenderer.forceContextLoss();
			decompressRenderer.dispose();
			decompressRenderer = null;
		}

		if (result instanceof ArrayBuffer) {
			const manifest = options._manifest ?? null;
			const skyKey = config.skyTextureKey;
			const skyExpected = Boolean(skyKey && skyKey !== 'background');
			const validation = validateGlbOutputCfn({ buffer: result, manifest, includeSky, skyExpected });
			if (!validation.valid) {
				throw new ExportValidationError(
					`GLB export validation failed: ${validation.missing.join(', ')}`,
					validation.missing
				);
			}
			return result;
		}
		return null;
	}

	// Interactive download path — exports full scene (legacy behavior)
	const originalBackground = scene.background;
	const originalScale = scene.scale.clone();
	const carpetWasInScene = carpet && scene.children.includes(carpet);

	try {
		if (carpet && carpetWasInScene && !includeFloor) {
			scene.remove(carpet);
		}

		if (backgroundColor) {
			scene.background = backgroundColor;
		}

		scene.scale.set(scale, scale, scale);
		scene.updateWorldMatrix(false, true);

		const exporter = new GLTFExporter();
		const exportOptions = {
			trs: false,
			onlyVisible: true,
			maxTextureSize,
			binary: format === 'glb'
		};

		const result = await new Promise<ArrayBuffer | object>((resolve, reject) => {
			exporter.parse(
				scene,
				(data) => resolve(data as ArrayBuffer | object),
				(error) => reject(error),
				exportOptions
			);
		});

		triggerDownload(result, filename, format);

		return result instanceof ArrayBuffer ? result : null;
	} finally {
		scene.scale.copy(originalScale);
		scene.updateWorldMatrix(false, true);

		if (carpet && carpetWasInScene && !includeFloor) {
			scene.add(carpet);
		}

		if (skyTextureKey === 'background' && backgroundColor) {
			scene.background = backgroundColor;
		} else if (environmentMapTexture) {
			scene.background = environmentMapTexture;
		} else {
			scene.background = originalBackground;
		}
	}
};
